import store from "@/store";
import axios from "axios";
import configService from "../configs/config.js";
import loginService from "../services/Login";

const nodeInstance = axios.create({
  baseURL: configService.apiNodeUrl,
  timeout: 15000,
});

const phpInstance = axios.create({
  baseURL: configService.apiUrl,
});

const gestorInstance = axios.create({
  baseURL: configService.apiGestorUrl,
});

let numLoadings = 0;

const getNodeToken = () =>
  store.getters.getJwt || store.getters.getTokenControllerNode;

const getPhpToken = () =>
  store.getters.getToken || store.getters.getTokenControllerPhp;

const clearSessionState = () => {
  store.dispatch("fetchAuth", false);
  store.dispatch("fetchToken", null);
  store.dispatch("fetchJwt", null);
  store.dispatch("fetchTokenControllerNode", null);
  store.dispatch("fetchTokenControllerPhp", null);
  store.dispatch("fetchUser", null);
  store.dispatch("fetchUserRole", null);
};

const hasExplicitAuthorization = (request) =>
  Boolean(request?.headers?.Authorization || request?.headers?.authorization);

// Requests con { silent: true } en la config no muestran el spinner global
// (usado por pollings periódicos, p.ej. sub-salas de networking)
const trackLoading = (request) => {
  if (request?.silent) return;
  numLoadings++;
  store.dispatch("fetchLoading", true);
};

const releaseLoading = (config) => {
  if (config?.silent) return;
  if (--numLoadings <= 0) {
    numLoadings = 0;
    store.dispatch("fetchLoading", false);
  }
};

const requestHandler = (request) => {
  const jwt = getNodeToken();
  if (hasExplicitAuthorization(request)) {
    trackLoading(request);
    return request;
  }
  if (jwt) {
    request.headers["Authorization"] = `Bearer ${jwt}`;
  } else {
    delete request.headers["Authorization"];
  }
  trackLoading(request);
  return request;
};

const phpRequestHandler = (request) => {
  const token = getPhpToken();
  if (hasExplicitAuthorization(request)) {
    trackLoading(request);
    return request;
  }
  if (token) {
    request.headers["Authorization"] = `Bearer ${token}`;
  } else {
    delete request.headers["Authorization"];
  }
  trackLoading(request);
  return request;
};

const gestorRequestHandler = (request) => {
  const jwt = getNodeToken();
  if (hasExplicitAuthorization(request)) {
    trackLoading(request);
    return request;
  }
  if (jwt) {
    request.headers["Authorization"] = `Bearer ${jwt}`;
    gestorInstance.defaults.headers.common["Authorization"] = `Bearer ${jwt}`;
  } else {
    delete request.headers["Authorization"];
    delete gestorInstance.defaults.headers.common["Authorization"];
  }
  trackLoading(request);
  return request;
};

const responseHandler = (response) => {
  releaseLoading(response.config);
  if (response.data) {
    return response.data;
  }
  return response;
};
const errorHandler = async (error) => {
  if (!error.response) {
    // timeout o red caída — bajar el spinner igualmente
    releaseLoading(error.config);
    return Promise.reject(error);
  }
  if (error.response.status === 401) {
    const phpToken = getPhpToken();
    const requestUrl = error.config?.url || "";

    clearSessionState();

    if (phpToken && !requestUrl.includes("/logout")) {
      try {
        await loginService.logout(phpToken);
      } catch (error) {
        console.error("Error on logoff monitor", error);
      }
    }
  }
  releaseLoading(error.config);

  return Promise.reject(error);
};

nodeInstance.interceptors.request.use((request) => requestHandler(request));
nodeInstance.interceptors.response.use(
  (response) => responseHandler(response),
  (error) => errorHandler(error)
);

phpInstance.interceptors.request.use((request) => phpRequestHandler(request));
phpInstance.interceptors.response.use(
  (response) => responseHandler(response),
  (error) => errorHandler(error)
);

gestorInstance.interceptors.request.use((request) =>
  gestorRequestHandler(request)
);
gestorInstance.interceptors.response.use(
  (response) => responseHandler(response),
  (error) => errorHandler(error)
);

export { gestorInstance, nodeInstance, phpInstance };
